import { mainEndpoints } from '@/lib/api/endpoints'
import { mainApi } from '@/lib/api/http'
import type { PageResult } from '@/lib/api/common'

import type {
  ApprovalLogQuery,
  CreateTableBody,
  CreateZoneBody,
  ExceptionQuery,
  PosApprovalPolicy,
  PosApprovalRequest,
  PosException,
  PosExceptionDetail,
  PosFloor,
  PosRegister,
  PosSession,
  PosTable,
  PosXReport,
  PosZone,
  PosZReport,
  RegisterQuery,
  SessionQuery,
  TableQuery,
  UpdateApprovalPolicyBody,
  UpdateTableBody,
  UpdateZoneBody,
  ExceptionResolution,
} from '../types'

const pos = mainEndpoints.pos

/* -------------------------------------------------------------------------- */
/* Registers                                                                  */
/* -------------------------------------------------------------------------- */

export async function fetchRegisters(query: RegisterQuery): Promise<PageResult<PosRegister>> {
  // Undefined keys are dropped by axios, so an unset filter sends nothing
  // rather than an empty string the DTO would reject.
  return await mainApi.get<PageResult<PosRegister>>(pos.registers, { params: query })
}

/**
 * Take a till out of service.
 *
 * There is no create to pair with this. A register is claimed by the DEVICE on
 * first sign-in — a till that had to be provisioned from here before it could
 * sell is a till that cannot replace a dead tablet mid-service.
 */
export async function retireRegister(id: string): Promise<PosRegister> {
  return await mainApi.post<PosRegister>(`${pos.registers}/${id}/retire`)
}

/* -------------------------------------------------------------------------- */
/* Floor                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * The grid, grouped server-side. A bare array, not the paged envelope.
 *
 * One group per outlet, so several outlets are a longer answer rather than a
 * different shape — `FloorGrid` already draws a heading per outlet.
 */
export async function fetchFloor(branchIds?: readonly string[]): Promise<PosFloor[]> {
  const rows = await mainApi.get<PosFloor[]>(pos.floor, {
    params: branchIds?.length ? { branchIds } : undefined,
  })

  return rows ?? []
}

export async function fetchTables(query: TableQuery): Promise<PageResult<PosTable>> {
  return await mainApi.get<PageResult<PosTable>>(pos.tables, { params: query })
}

export async function createTable(body: CreateTableBody): Promise<PosTable> {
  return await mainApi.post<PosTable>(pos.tables, body)
}

export async function updateTable(id: string, body: UpdateTableBody): Promise<PosTable> {
  return await mainApi.patch<PosTable>(`${pos.tables}/${id}`, body)
}

export async function deleteTable(id: string): Promise<void> {
  await mainApi.delete<void>(`${pos.tables}/${id}`)
}

export async function fetchZones(branchIds?: readonly string[]): Promise<PosZone[]> {
  const rows = await mainApi.get<PosZone[]>(pos.zones, {
    params: branchIds?.length ? { branchIds } : undefined,
  })

  return rows ?? []
}

export async function createZone(body: CreateZoneBody): Promise<PosZone> {
  return await mainApi.post<PosZone>(pos.zones, body)
}

export async function updateZone(id: string, body: UpdateZoneBody): Promise<PosZone> {
  return await mainApi.patch<PosZone>(`${pos.zones}/${id}`, body)
}

export async function deleteZone(id: string): Promise<void> {
  await mainApi.delete<void>(`${pos.zones}/${id}`)
}

/* -------------------------------------------------------------------------- */
/* Sessions                                                                   */
/* -------------------------------------------------------------------------- */

export async function fetchSessions(query: SessionQuery): Promise<PageResult<PosSession>> {
  return await mainApi.get<PageResult<PosSession>>(pos.sessions, { params: query })
}

/**
 * The X report for one drawer.
 *
 * A GET, and it means it: taking an X writes nothing, which is what makes it
 * safe to poll while a drawer trades. On a session already counted the same call
 * re-derives the period from the orders as they stand now — a second opinion
 * against the frozen Z, not a replacement for it.
 */
export async function fetchXReport(sessionId: string): Promise<PosXReport> {
  return await mainApi.get<PosXReport>(`${pos.sessions}/${sessionId}/x-report`)
}

/**
 * The Z report for one drawer, read back from the frozen document.
 *
 * `report` comes back null — not as an error — on a session still trading and on
 * a force-close that ended a period before any till reported. Both are ordinary
 * rows in the session list, and a call that threw on them would make them look
 * broken.
 */
export async function fetchZReport(sessionId: string): Promise<PosZReport> {
  return await mainApi.get<PosZReport>(`${pos.sessions}/${sessionId}/z-report`)
}

/**
 * End a trading period no device will close.
 *
 * The reason is required by the API, not by politeness: this is a write against
 * a drawer a tablet may still be holding, and "who ended my session, and why"
 * has to have an answer.
 */
export async function forceCloseSession(id: string, reason: string): Promise<PosSession> {
  return await mainApi.post<PosSession>(`${pos.sessions}/${id}/force-close`, { reason })
}

/* -------------------------------------------------------------------------- */
/* Exceptions                                                                 */
/* -------------------------------------------------------------------------- */

export async function fetchExceptions(query: ExceptionQuery): Promise<PageResult<PosException>> {
  return await mainApi.get<PageResult<PosException>>(pos.exceptions, { params: query })
}

/** One exception with `payload` — the upload exactly as it arrived. */
export async function fetchException(id: string): Promise<PosExceptionDetail> {
  return await mainApi.get<PosExceptionDetail>(`${pos.exceptions}/${id}`)
}

export async function resolveException(
  id: string,
  resolution: ExceptionResolution,
  note?: string,
): Promise<PosExceptionDetail> {
  return await mainApi.post<PosExceptionDetail>(`${pos.exceptions}/${id}/resolve`, {
    resolution,
    note,
  })
}

export async function discardException(id: string, note?: string): Promise<PosExceptionDetail> {
  return await mainApi.post<PosExceptionDetail>(`${pos.exceptions}/${id}/discard`, { note })
}

/* -------------------------------------------------------------------------- */
/* Approvals                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Omit `branchId` for the tenant default every outlet without its own follows.
 *
 * Singular, and staying that way while the lists beside it took a list: a policy
 * is ONE outlet's rules or the estate's default, and "the policy for these three
 * outlets" is not a thing that exists. The screen handles a multi-outlet
 * selection by declining to edit rather than by picking one — see `PosPage`.
 */
export async function fetchApprovalPolicy(branchId?: string): Promise<PosApprovalPolicy> {
  return await mainApi.get<PosApprovalPolicy>(pos.approvalPolicy, { params: { branchId } })
}

/**
 * PUT, and the whole rule set at once.
 *
 * A screen of nine switches that patched one of them would race itself the
 * moment two managers had it open — the second save silently reverting the
 * first's other eight.
 */
export async function updateApprovalPolicy(
  body: UpdateApprovalPolicyBody,
): Promise<PosApprovalPolicy> {
  return await mainApi.put<PosApprovalPolicy>(pos.approvalPolicy, body)
}

export async function fetchApprovalLog(
  query: ApprovalLogQuery,
): Promise<PageResult<PosApprovalRequest>> {
  return await mainApi.get<PageResult<PosApprovalRequest>>(pos.approvalLog, { params: query })
}
